import { Timestamp } from "firebase/firestore";

export type PreparedFile = {
    name: string;
    type: string;
    size: number;
    data: string;
}

// ============================= ELEMENT TYPES =============================
export type ElementPosition = {
    top: number;
    left: number;
    width: number;
}

// ============================= CORE/DOC TYPES =============================

export enum HistoryEntryType {
    CREATE = 'create',
    MODIFY = 'modify',
    DELETE = 'delete',
    RESTORE = 'restore',
    DRAFT = 'draft',
}
export type HistoryEntry = {
    type: HistoryEntryType;
    userId: string;
    user?: AccountPublic;
    timestamp?: Timestamp | null;
    ids: {
        productid?: string;
        categoryId?: string;
        locationId?: string;
        customerId?: string;
        supplierId?: string;
        transactionId?: string;
        actionId?: string;
    }
    docType?: string;
}

export type HistoryTimestamps = {
    createdAt?: Timestamp | null;  // Firestore Timestamp
    deletedAt?: Timestamp | null;  // Firestore Timestamp
    modifiedAt?: Timestamp | null;  // Firestore Timestamp
}

export type DocProps = {
    id?: string;
    isDeleted?: boolean;
    isDraft?: boolean;
    keyHistory?: HistoryTimestamps;
    createdById?: string; // Should be used for transaction and product action.
    createdBy?: AccountPublicRead; // Should be used for transaction and product action.
}

// ============================= REUSABLE TYPES =============================

export type ContactInfo = {
    name?: string;
    email: string;
    phone: string;
    note?: string;
}

export type PostalAddress = {
    address: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    co: string; // Care of
}

// ============================= HOOK TYPES =============================

export enum DeviceSize {
    SM = 'sm',
    MD = 'md',
    LG = 'lg',
}

// ============================= USER/AUTH TYPES =============================

export enum AuthenticationStatus {
    online = 'online',
    offline = 'offline',
}

export enum AuthenticationMethod {
    password = 'password',
    google = 'google.com',
}

export type AccountPrivateInfo = DocProps & {
    isBusiness: boolean;
    org: {
        orgName: string;
        orgNum: string;
        vatNum: string;
    }
    postalAddress: PostalAddress;
    contact: {
        contactPerson: string;
        phone: string;
    }
    counters?: {
        notificationCounter: number;
    };
    notifications?: Record<string, Notification>;
}

export type AccountPrivateInfoWrite = AccountPrivateInfo

export type AccountPrivateInfoRead = AccountPrivateInfo & {
    accessInvitations?: string[];   // Paths to access invitations
    storageIds?: string[];          // Simply the ids of storages (not paths)
}

export type AccountPublic = DocProps & {
    _version: 1;
    id: string;
    name: string;
    other: {
        avatarColor: string;
        avatarUrl?: string;
        hasAvatar?: boolean;
    };
}

export type AccountPublicWrite = {
    _version?: 1;
    id: string;
    name?: string;
    file?: File | null;
    other: {
        avatarColor: string;
        hasAvatar?: boolean;
    };
}

export type AccountPublicRead = DocProps & {
    _version?: 1;
    id: string;
    name?: string;
    other: {
        avatarColor: string;
        avatarUrl?: string;
        hasAvatar?: boolean;
        isComplete: boolean;
    };
}

// ============================= STORAGE & CATEGORY & CUSTOM FIELDS TYPES =============================

export enum StoragePlan {
    free = 'free',
    pro = 'pro',
    premium = 'premium',
}

export enum CustomInputTypeEnum {
    TEXT = 'text',
    NUMBER = 'number',
    CHECKBOX = 'checkbox',
    TEXTAREA = 'textarea',
    SELECT = 'select',
    RADIO = 'radio',
}

export type CustomInputAttributes = 
    | { type: CustomInputTypeEnum.TEXT; required: boolean; defaultValue: string; }
    | { type: CustomInputTypeEnum.NUMBER; required: boolean; defaultValue: number; }
    | { type: CustomInputTypeEnum.CHECKBOX; required: boolean; defaultValue: boolean; }
    | { type: CustomInputTypeEnum.TEXTAREA; required: boolean; defaultValue: string; }
    | { type: CustomInputTypeEnum.SELECT; required: boolean; defaultValue: string; options: string[]; }
    | { type: CustomInputTypeEnum.RADIO; required: boolean; defaultValue: string; options: string[]; }

export type CustomInputGroup = {
    customInputOrder: string[];
    customInputs: Record<string, CustomInputAttributes>;
}

export type CustomInputGroups = Record<string, CustomInputGroup>;

export type CustomInput = Record<string, Record<string, string | boolean | number>>;

export type CustomInputWrite = {
    ids: {
        storageId: string;
        categoryId: string;
    };
    customInputs: {
        customInputGroups: CustomInputGroups;
        customInputGroupOrder: string[];
    };
}

export type QueryAttributeMatch = Record<string, string[]>;

export type StorageSettings = {
    storagePlan: StoragePlan;
    enableTransactions: boolean;
    enableCustomersAndSuppliers: boolean;
    requireActionsLinkedToTransactions: boolean;
    enableStockAdjustment: boolean;
    defaultProductUnit: string;
}

export type Storage = DocProps & {
    _version: 1;
    name: string;
    description: string;
    location: string;
    settings?: StorageSettings;
    instantDeletion?: boolean;
}

export type StorageWrite = Storage

export type StorageRead = Storage & {
    productAmount?: number;
    ownerIds?: string[];
    lastAdded: {
        sku?: string;
        categoryId?: number;
        locationId?: number;
        customerId?: number;
        supplierId?: number;
        transactionId?: number;
    };
    productsStockStatus: {
        highStock?: number;
        lowStock?: number;
        noStock?: number;
        negativeStock?: number;
    };
    counters: {
        productCounter?: number;
        categoryCounter?: number;
        locationCounter?: number;
        customerCounter?: number;
        supplierCounter?: number;
        transactionCounter?: number;
    };
    usage?: {
        aiProductSuggestions?: {
            date: string;
            count: number;
        };
    };
    historyEntries?: Record<string, HistoryEntry>; // Key is history entry id and holds history for storage, category, customer, supplier, product, transaction, product action.
}

export type Category = DocProps & {
    _version: 1;
    categoryId: number;
    name: string;
    description: string;
    storageRequirements: string;
    location?: string;
    notes: string;
    customInputs?: {
        customInputGroups?: CustomInputGroups;
        customInputGroupOrder?: string[];
    };
    relations: {
        parentCategory?: string;
        childCategories?: Category[];
    };
}

export type CategoryWrite = Category & {
    ids: {
        storageId: string;
        categoryId?: string;
    }
}

export type CategoryRead = Category & {
    ids: {
        storageId: string;
        categoryId?: string;
    }
}

export type Location = DocProps & {
    _version: 1;
    name: string;
    description: string;
    notes: string;
    relations: {
        parentLocation?: string;
        childLocations?: Location[];
    };
}

export type LocationWrite = Location & {
    ids: {
        storageId: string;
        locationId?: string;
    }
}

export type LocationRead = Location & {
    ids: {
        storageId: string;
        locationId?: string;
    }
}

// ============================= PRODUCT TYPES =============================

export type ProductImage = {
    productImageId: string;
    url: string;
    order: number;
    fileName: string;
    storagePath?: string; // Storage path for deletion
    uploadedAt: Timestamp;
}

export type ProductAmountHistoryEntry = DocProps & {
    amount: number;
    delta: number;
    actionType: 'increment' | 'decrement' | 'adjust';
    ids?: {
        actionId?: string;
        transactionId?: string;
        userId?: string;
    };
}

export enum ProductStatus {
    PRODUCT_HIGH_STOCK = 'highStock',
    PRODUCT_LOW_STOCK = 'lowStock',
    PRODUCT_NO_STOCK = 'noStock',
    PRODUCT_NEGATIVE_STOCK = 'negativeStock',
}

export type Product = DocProps & {
    _version: 1;
    sku: string;
    name: string;
    description: string;
    location?: string;
    unit: string;
    stockSettings: {
        allowNegativeInventory: boolean;
        lowStockWarning?: number;
        responsibleUserId: string;
    };
    customInputs?: CustomInput;
    status?: {
        currentAmount?: number;
        amountHistory?: ProductAmountHistoryEntry[];
        current: ProductStatus;
    };
    counters?: {
        actionsCount?: number;
    };
    barcodes?: string[];
}

export type BarcodeRead = {
    _version: 1;
    barcode: string;
    sku: string;
    productId: string;
}

export type ProductRead = Product & {
    categories: string[];
    locations?: string[];
    actionAmount?: number;
    images?: ProductImage[];
}

export type ProductWrite = Product & {
    ids?: {
        storageId: string;
        productId?: string;
    };
    categories?: string[];
    locations?: string[];
    images?: {
        files?: File[] | PreparedFile[]; // For new images
        existingImages?: ProductImage[]; // For editing: current images
        imagesToDelete?: string[]; // For editing: image IDs to delete
    };
}

export type ProductAiSuggestion = {
    sku: string;
    name: string;
    description: string;
    unit: string;
    barcodes: string[];
    categories: string[];
    locations: string[];
    stockSettings: {
        allowNegativeInventory: boolean;
        lowStockWarning?: number;
    };
    customInputs: CustomInput;
}

export enum ProductActionType {
    INCREMENT = 'increment',
    DECREMENT = 'decrement',
    ADJUST = 'adjust',
}
export type ProductAction = DocProps & {
    ids?: {
        storageId: string;
        productId?: string;
        actionId?: string;
    };
    type: ProductActionType;
    amount: number;
    description?: string;
    transaction?: {
        transactionId?: number; // Note that this is not the document id
        id?: string;
    };
    supplier?: {
        supplierId?: number; // Note that this is not the document id
        id?: string;
    };
    customer?: {
        customerId?: number; // Note that this is not the document id
        id?: string;
    };
}
export type ProductActionRead = ProductAction & {
    createdBy: string;
}
export type ProductActionWrite = ProductAction


// ============================= CUSTOMER TYPES =============================

export type Customer = DocProps & {
    _version: 1;
    customerId: number;
    name: string;
    notes: string;
    contactInformation: ContactInfo
}

export type CustomerWrite = Customer & {
    ids: {
        storageId: string;
        customerId?: string;
    }
}

export type CustomerRead = Customer;

// ============================= SUPPLIER TYPES =============================

export type Supplier = DocProps & {
    _version: 1;
    supplierId: number;
    name: string;
    notes: string;
    contactInformation: ContactInfo
}

export type SupplierWrite = Supplier & {
    ids: {
        storageId: string;
        supplierId?: string;
    }
}

export type SupplierRead = Supplier;

// ============================= TRANSACTION TYPES =============================

export enum TransactionType {
    INCOMING = 'incoming',
    OUTGOING = 'outgoing',
}
export type Transaction = DocProps & {
    _version: 1;
    transactionId: number;
    type: TransactionType;
    customer?: {
        customerId?: string;
        customerName?: string;
    };
    supplier?: {
        supplierId?: string;
        supplierName?: string;
    };
    description?: string;
    actions?: {
        actionDocs?: ProductAction[];
        actionIds?: string[];
        actionPaths?: string[]; // Paths to action documents
    };
    isFinalized: boolean;
}

export type TransactionRead = Transaction;

export type TransactionWrite = Transaction & {
    ids: {
        storageId: string;
        transactionId?: string;
    };
}

// ============================= ACCESS TYPES =============================

export enum AccessRole {
    OWNER = 'owner',
    VIEWER = 'viewer',
    EDITOR = 'editor',
    WORKER = 'worker',
}
    
export enum AccessStatus {
    PENDING = 'pending',
    ACCEPTED = 'accepted',
    DECLINED = 'declined',
    _ = '_',
}

export type AccessInvitation = DocProps & {
    _version: 1;
    role: AccessRole;
    storage: {
        storageId?: string;
        storageName: string;
    };
    status: AccessStatus;
    invitation: {
        message?: string;
        email: string;
    };
    userId: string;
    user?: AccountPublic;
}

export type AccessInvitationWrite = AccessInvitation & {
    processed: boolean;
}

export type AccessInvitationRead = AccessInvitation;

// ============================= NOTIFICATION TYPES =============================

export enum NotificationType {
    ACCESS_INVITATION = 'accessInvitation',     // For access invitations
    ACCESS_INVITATION_RESPONSE = 'accessInvitationResponse', // Accept / decline / delete
    STOCK_WARNING = 'stockWarning',             // For when stock is low
    STOCK_ALERT = 'stockAlert',                 // For when stock is out or negative
    SYSTEM_MESSAGE = 'systemMessage',           // For system-related messages
    WELCOME = 'welcome',
}

export type Notification = DocProps & {
    _version: 1;
    type: string;
    isRead: boolean;
    ids?: {
        storageId?: string;
        accessId?: string;
        productId?: string;
    };
    storageDocId?: string;
    itemDocId?: string;
    title?: string;
    message?: string;
    createdAt?: number | null;
}

// ============================= UI/INPUT TYPES =============================

export enum StockStatus {
    ALL = 'all',
    IN_STOCK = 'inStock',
    OUT_OF_STOCK = 'outOfStock',
    NEGATIVE_STOCK = 'negativeStock',
    LOW_STOCK = 'lowStock',
}

export type TagSelector = Record<string, TagSelectorEntry>;

export type TagSelectorEntry = {
    primary: string;
    secondary?: string;
    keywords?: string[];
}

export type Dialog = Record<string, unknown> | undefined;

export enum InputMaxLength {
    short = 40,
    medium = 80,
    long = 200,
    extraLong = 500,
}

// ============================= ERROR TYPES =============================

export type FirebaseError = {
    code: string;
    message: string;
}

export type StorageError = Error | FirebaseError;

// ============================= SORTING TYPES =============================

export type SortableValue = string | number | boolean | null | undefined;

export enum SortDirection {
    ASCENDING = 'asc',
    DESCENDING = 'desc',
}

// ============================= FORM/TOAST STATUS =============================

export type Toast = {
    text: string;
    colorScheme: 'green' | 'red' | 'yellow' | 'blue' | 'gray';
}

export enum FormStatus {
    IDLE = 'idle',
    LOADING = 'loading',
    ERROR = 'error',
    SUCCESS = 'success',
}